/**
 * Detalhe da Meta no corpo dos erros da Graph (`GRAPH_API_ERROR`/`META_TOKEN_ERROR`/`META_PERMISSION_ERROR` 502,
 * `META_RATE_LIMIT` 429), no TOPO do corpo, ao lado de `detail`/`error` (contrato `erros-meta` §3). Ausente =
 * back anterior ao contrato.
 */
export interface MetaDoErro {
  code?: number
  cause?: string
  message?: string
  details?: string
}

/**
 * Extras de TOPO dos erros de `/access-policies` (faixa 28, back `handlers.py::_politica_invalida`/`_politica_lockout`):
 * `chave` do 422 `POLICY_INVALID` (o campo recusado) e `ip` do 422 `POLICY_LOCKOUT` (o IP do editor). `ip: null` no
 * corpo (IP não identificado) fica AUSENTE — a tela mostra o `detail` como veio.
 */
export interface ExtrasDoErro {
  chave?: string
  ip?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly retryAfterSeconds?: number
  /** Id do disparo que já existe, no 409 `duplicate_request`. Opcional POR CONTRATO:
   *  back anterior a esta migração não manda a chave. */
  readonly dispatchId?: string
  /** `meta_*` do corpo dos erros da Graph; ausente em back velho e em qualquer outro erro. */
  readonly meta?: MetaDoErro
  /**
   * `true` quando o `code` foi AFIRMADO — veio do corpo (`error` do envelope ou o
   * `detail.error` do detail-objeto) ou foi sintetizado de propósito pelo client (HTML no
   * lugar de JSON, `client.ts::onResponse`). `false` quando foi DEDUZIDO do status
   * (`STATUS_FALLBACK`, `VALIDATION`, `UNKNOWN`).
   *
   * Existe porque o mesmo `code` nasce dos dois jeitos com sentidos opostos: o 401
   * "Invalid Credentials" de `/auth/login` e `/auth/change-password` vem SEM `error`
   * (senha errada, token vale) e o parser o preenche como `UNAUTHENTICATED`; o 401 de
   * token morto vem COM `error: "UNAUTHENTICATED"` (back `handlers.py::_unauthenticated`).
   * Dois estados, nunca `undefined`.
   */
  readonly codigoExplicito: boolean
  /** `chave` do 422 `POLICY_INVALID`. Ausente em qualquer outro erro. */
  readonly chave?: string
  /** `ip` do 422 `POLICY_LOCKOUT`. Ausente quando o back mandou `null` ou em qualquer outro erro. */
  readonly ip?: string

  constructor(
    status: number,
    code: string,
    detail: string,
    retryAfterSeconds?: number,
    dispatchId?: string,
    meta?: MetaDoErro,
    codigoExplicito = false,
    extras: ExtrasDoErro = {},
  ) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
    this.dispatchId = dispatchId
    this.meta = meta
    this.codigoExplicito = codigoExplicito
    this.chave = extras.chave
    this.ip = extras.ip
  }

  get detail(): string {
    return this.message
  }
}

const STATUS_FALLBACK: Record<number, string> = {
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'DUPLICATE',
  422: 'VALIDATION',
}

function textoDaMeta(valor: unknown): string | undefined {
  if (typeof valor !== 'string') return undefined
  const limpo = valor.trim()
  return limpo === '' ? undefined : limpo
}

/** `meta_*` do TOPO do corpo. Tipo errado é ignorado campo a campo e nunca lança; nenhum válido → `undefined`. */
function lerMetaDoErro(data: unknown): MetaDoErro | undefined {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined
  const corpo = data as Record<string, unknown>
  const codigoCru = corpo.meta_code
  const meta: MetaDoErro = {
    code: typeof codigoCru === 'number' && Number.isFinite(codigoCru) ? codigoCru : undefined,
    cause: textoDaMeta(corpo.meta_cause),
    message: textoDaMeta(corpo.meta_message),
    details: textoDaMeta(corpo.meta_details),
  }
  return Object.values(meta).some((valor) => valor !== undefined) ? meta : undefined
}

/** `chave`/`ip` do TOPO do corpo. Só string não vazia; o resto é ignorado e nunca lança. */
function lerExtrasDoErro(data: unknown): ExtrasDoErro {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {}
  const corpo = data as Record<string, unknown>
  return { chave: textoDaMeta(corpo.chave), ip: textoDaMeta(corpo.ip) }
}

export function parseApiError(status: number, data: unknown): ApiError {
  if (data && typeof data === 'object' && 'detail' in data) {
    const { detail } = data as { detail: unknown; error?: string }

    // Detail-objeto: {detail: {error, message, ...}}. Nasceu do 409 duplicado do
    // send/template e hoje serve TODO erro tipado do back (409 CONTACT_INACTIVE,
    // 422 CONTACT_BLOCKED, 422 CONTACT_DELETED, 422 TEMPLATE_NOT_SENDABLE). O default NÃO pode ser a
    // copy de nenhum deles — quem dá copy por código é `friendlyMessage`.
    if (detail && typeof detail === 'object' && !Array.isArray(detail) && 'error' in detail) {
      const d = detail as {
        error: string
        message?: string
        retry_after_seconds?: number
        dispatch_id?: string
      }
      return new ApiError(
        status,
        d.error,
        d.message ?? 'Não foi possível concluir a operação.',
        d.retry_after_seconds,
        d.dispatch_id,
        undefined,
        true,
      )
    }

    /**
     * 422 de validação (query OU body) tem OUTRO shape: o back não registra handler de
     * `RequestValidationError`, então validação de query cai no default do
     * FastAPI — `{detail: [{type, loc: ['query' | 'body', ...], msg, ...}]}`, sem
     * as chaves `error`/`error_id` do envelope da casa. O parser aceita os dois.
     *
     * O `msg` e o nome do param entram na mensagem porque "Dados inválidos na
     * requisição" não diz ao usuário QUAL campo corrigir — e num formulário de
     * filtros com oito controles, isso é a diferença entre consertar e
     * abandonar.
     */
    if (Array.isArray(detail)) {
      const primeiro = detail[0] as { loc?: unknown[]; msg?: string } | undefined
      const msg = primeiro?.msg
      // `loc` é `['query', '<param>']` em query e `['body', ...caminho]` em body. O último item
      // nomeia o campo — exceto em item de lista, onde é o ÍNDICE (`[..., 'parameters', 1]`) e
      // só o `msg` vai pra tela (o back faz o `msg` se auto-nomear nesses casos).
      const param = Array.isArray(primeiro?.loc) ? primeiro.loc.at(-1) : undefined
      const texto =
        msg && typeof param === 'string'
          ? `${param}: ${msg}`
          : (msg ?? 'Dados inválidos na requisição')
      return new ApiError(status, 'VALIDATION', texto)
    }

    if (typeof detail === 'string') {
      // `error` presente = código AFIRMADO pelo back; ausente = deduzido do status (ver `codigoExplicito`).
      const erro = (data as { error?: unknown }).error
      const explicito = typeof erro === 'string'
      const code = explicito ? erro : (STATUS_FALLBACK[status] ?? 'UNKNOWN')
      // Os 4 erros da Graph saem por aqui (`detail` = `str(exc)`, back `app/exceptions/handlers.py:774-818`).
      return new ApiError(status, code, detail, undefined, undefined, lerMetaDoErro(data), explicito, lerExtrasDoErro(data))
    }
  }
  return new ApiError(status, STATUS_FALLBACK[status] ?? 'UNKNOWN', `Erro inesperado (HTTP ${status})`)
}

export const GOVERNANCE_MESSAGES: Record<string, string> = {
  TARGET_NOT_BELOW_EDITOR: 'Você só pode gerenciar usuários com role abaixo da sua.',
  ROLE_GRANT_REQUIRES_SUPERIOR: 'Você não pode conceder uma role igual ou acima da sua.',
  UNKNOWN_ROLE: 'Role desconhecida.',
  SUBROLE_CASCADE_VIOLATION: 'A subrole contém permissões que você mesmo não possui.',
  SUBROLE_SCOPE_CASCADE_VIOLATION: 'A subrole contém escopos fora do seu acesso.',
  SUBROLE_MINIMUM_ROLE_TOO_HIGH: 'A role mínima da subrole está acima da sua role.',
  SUBROLE_ROLE_BELOW_MINIMUM: 'Sua role não atinge a role mínima desta subrole.',
  SUBROLE_DEFAULT_LOCKED: 'A subrole padrão do tenant está travada — defina outra default antes.',
  SUBROLE_HAS_USERS_FORCE_REQUIRED: 'A subrole tem usuários atribuídos — confirme a remoção forçada.',
  SUBROLE_CONFIRM_NAME_MISMATCH: 'O nome digitado não confere com o nome da subrole.',
  SUBROLE_INACTIVE: 'Esta subrole está inativa.',
  DECISION_TREE_IN_USE: 'Esta árvore é a árvore padrão de um ou mais canais — vincule outra árvore a eles antes de excluir.',
  CHANNEL_NOT_OWNED_BY_PHONE: 'O canal selecionado pertence a outro número de WhatsApp.',
  CONTACT_INACTIVE: 'Este contato está na lixeira. Restaure o contato antes de editar.',
  /* 422 do ENVIO (`contacts/sendability.py:92-93`), distinto do 409
     `CONTACT_INACTIVE` acima, que é MUTAÇÃO em contato deletado. Mesma
     lixeira, ações diferentes — a copy tem que dizer qual das duas o
     operador tentou. O `detail` do back é só "Contato deletado": verdadeiro
     e sem saída. */
  CONTACT_DELETED: 'Este contato está na lixeira. Restaure o contato para voltar a enviar.',
  CONTACT_BLOCKED: 'Este contato está bloqueado neste número. Desbloqueie no painel do contato para voltar a enviar.',
  TEMPLATE_NOT_SENDABLE: 'Este template não pode ser enviado — está arquivado ou pausado.',
  DISPLAY_NAME_REQUEST_PENDING: 'Já existe um pedido de nome em análise pela Meta. Cancelar só pelo Business Manager.',
  DISPLAY_NAME_NOT_APPROVED: 'Nenhum nome aprovado para aplicar.',
  PIN_MISMATCH: 'PIN incorreto.',
  REGISTER_RATE_LIMITED: 'Limite da Meta: 10 registros do número em 72 horas.',
  WABA_NOT_CONFIGURED: 'Conta WhatsApp ainda não sincronizada para este tenant.',
}

export function friendlyMessage(err: ApiError): string {
  if (err.code === 'duplicate_request') {
    // O TTL da idempotência (`err.retryAfterSeconds`) NÃO entra na copy de propósito:
    // reenviar depois dele duplica as mensagens de verdade (handoff §2.1). O front
    // nunca faz retry automático deste código.
    return (
      'Este disparo já foi feito — o mesmo lote saiu há pouco. Não reenvie: ' +
      'mudar a lista de contatos ou o template libera um envio novo.'
    )
  }
  return GOVERNANCE_MESSAGES[err.code] ?? err.detail
}

/**
 * Predicados de status sobre um erro de query. O interceptor do `api` rejeita
 * SEMPRE com `ApiError` e **sem** `response` (é `ApiError`, não `AxiosError`) —
 * classificar por `error.response?.status` aqui seria código morto.
 *
 * Existem como função exportada porque a mesma checagem nasceu na 3ª trilha de
 * histórico (contato, lista de transmissão, usuário) e as três precisam separar
 * "não posso ver" de "deu erro": lista vazia no lugar de 403 é uma AFIRMAÇÃO
 * falsa sobre o registro.
 */
export function ehForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403
}

export function ehNaoEncontrado(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

/**
 * 403 `NETWORK_FORBIDDEN` (faixa 28 §4): o IP está fora da rede permitida. Pelo `error`, NUNCA só pelo status — 403
 * `ORIGIN_FORBIDDEN` é sessão morta e 403 `FORBIDDEN` é falta de grant/usuário inativo. O cookie de renovação vale.
 */
export function ehRedeBloqueada(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.code === 'NETWORK_FORBIDDEN'
}

/**
 * A chave do dispositivo não pôde ser lida/gerada/gravada (faixa 33, D1): o login NÃO sai — sintetizado pelo
 * interceptor (`client.ts`), status 0, `codigoExplicito`. Só o login vê este código; nas outras rotas a mesma falha é
 * `NETWORK` (falha local passageira, nunca sessão morta).
 */
export const CODIGO_DPOP_INDISPONIVEL = 'DPOP_INDISPONIVEL'

export const TEXTO_DPOP_INDISPONIVEL =
  'Este navegador não conseguiu guardar a identidade do dispositivo. Saia do modo anônimo ou libere o armazenamento do site e tente de novo.'

export function ehDpopIndisponivel(error: unknown): boolean {
  return error instanceof ApiError && error.code === CODIGO_DPOP_INDISPONIVEL
}
